// helper functions
import BACKENDERROR from "app/static/frontend/imports/backenderror"

// action types
import * as actionDicts from "app/static/frontend/dashboards/action-types"

// constants
import { dashboardPaths } from "app/static/frontend/imports/desktopPaths"

/**
 * This action creator:
 *      - updates the store to tell the Dashboard that we are saving
 *      - saves the Dashboard layout to the database
 *      - if we have an array of Widgets that have been deleted, invokes the widget_engine endpoint with a DELETE request
 *      - updates the store to tell the Dashboard that we are done saving
 *
 * @param {Function} createOrUpdateDashboard - action creator that creates or updates a Dashboard in the db
 * @param {Array[String]} deletedWidgets - array of Widget strings that have been deleted in the Dashboard (["ABCD", "CEBX", ...])
 * @param {string} id - the id of the Dashboard
 * @param {Function} sendAction - action creator for handling any request an action can handle (GET/POST/PATCH/DELETE)
 * @param {Function} setDashboardsValues - updates the given key value pairs in values for dashboard id in the store
 */
export const saveDashboard = async ({
    createOrUpdateDashboard,
    deletedWidgets,
    id,
    name,
    sendAction,
    setDashboardsValues,
    widgets,
}) => {
    setDashboardsValues({
        id,
        values: {
            isSaving: true,
        },
    })

    // save the layout (and name if necessary)
    await createOrUpdateDashboard({
        id,
        // the user can update the Dashboard name in the
        // app/static/frontend/dashboards/components/DashboardHeader.jsx HeaderInput component
        values: {
            name,
            widgets,
        },
    })

    // if we have deleted any widgets, we need to invoke the widget_engine
    // with a POST request
    if (deletedWidgets.length) {
        await sendAction(
            DjangIO.app.widgets.models.Dashboard.objects.get(id),
            actionDicts.deleteWidgetActionDict,
            {
                // we treat POST as DELETE
                // since DELETE requests are dissalowed throughout Quorum
                // at the nginx reverse proxy level of the stack
                method: "post",
                // name of @action method defined on the Resource
                action: "widget_delete",
                // schema
                kwargs: {
                    deletedWidgets
                    // delete requests are allowed to have a body
                    // https://stackoverflow.com/questions/14323716/restful-alternatives-to-delete-request-body
                    // in axios, the request.body for a DELETE request must be defined in config.data
                    // https://github.com/axios/axios/issues/897#issuecomment-343715381
                    // data: {
                    //     deletedWidgets,
                    // },
                },
                // redux payload
                payload: {},
            }
        )
    }

    setDashboardsValues({
        id,
        values: {
            isSaving: false,
        }
    })
}

export const refreshDashboard = async ({
    id,
    sendAction,
    setDashboardsValues,
}) => {
    setDashboardsValues({
        id,
        values: {
            isRefreshing: true,
        },
    })

    await sendAction(
        DjangIO.app.widgets.models.Dashboard.objects.get(id),
        actionDicts.clearDashboardCacheActionDict,
        {
            // we are modifying the Dashboard cache, so let's use patch
            method: "patch",
            // name of @action method defined on the Resource
            action: "clear_cache",
            // schema
            kwargs: {},
            // redux payload
            payload: {},
        }
    )

    setDashboardsValues({
        id,
        values: {
            isRefreshing: false,
        },
    })
}

/**
 * This action creator:
 *      - archives the Dashboard
 *      - clears the dashboard section of the redux-store
 *      - segues back to the DashboardTable resource table
 *
 * @param {Function} createOrUpdateDashboard - action creator that creates or updates a Dashboard in the db
 * @param {Function} destroyDashboardStore - destroys the Dashboard in the store
 * @param {string} id - the id of the Dashboard
 * @param {Function} segueToUrl - navigate to an internal quorum url
 */
export const archiveDashboard = async ({
    createOrUpdateDashboard,
    destroyDashboardStore,
    id,
    segueToUrl,
}) => {
    const willArchive = await swal({
        buttons: ["Cancel", "Archive"],
        title: "Are you sure you want to archive this Dashboard?",
        text: "It will no longer be accessible.",
        icon: "warning",
    })

    if (willArchive) {
        createOrUpdateDashboard({
            id,
            values: {
                archived: true,
            }
        })

        destroyDashboardStore({
            id,
        })

        segueToUrl(dashboardPaths.dashboardPrefix)
    }
}

/**
 * setOrRemoveDashboardAsHomepage - action creator that updates the homepage dashboard associated with a user on frontend and backend
 * @param {Boolean} removeHomepage - whether we want to remove the dashboard as the user's homepage
 * @param {Function} sendAction - action creator for handling any request an action can handle (GET/POST/PATCH/DELETE)
 * @param {Function} setDashboardsValues - updates the given key value pairs in values for dashboard id in the store
 * @param {Function} updateUserdata - action creator for updating userdata in the store
 */
export const setOrRemoveDashboardAsHomepage = async ({
    id,
    removeHomepage,
    sendAction,
    setDashboardsValues,
    updateUserdata,
}) => {
    const homepageDashboardId = !removeHomepage && id
    setDashboardsValues({
        id,
        values: {
            isSaving: true,
        },
    })
    try {
        await sendAction(
            DjangIO.app.userdata.models.QuorumUser,
            actionDicts.updateHomepageDashboardActionDict,
            {
                action: "update_user_information",
                kwargs: {
                    userId: Userdata.id,
                    updateFields: {
                        homepage_dashboard_id: homepageDashboardId || null,
                    },
                },
                method: "patch",
                payload: {
                    id,
                }
            }
        )
        // if send action fails, do not update userdata
        updateUserdata({ homepage_dashboard_id: homepageDashboardId || null })
    } catch (error) {
        BACKENDERROR(error)
    }
    setDashboardsValues({
        id,
        values: {
            isSaving: false,
        },
    })
}

/**
 * setWidgetsGlobalDate - action creator that updates the widgets with any applicable DateRangeType/custom date range
 * @param {int} dateRangeType - the DateRangeType enum value
 * @param {moment} globalDateCustomFrom - the "from" value in the global date dropdown
 * @param {moment} globalDateCustomTo - the "to" value in the global date dropdown
 * @param {string} id - the id of the Dashboard
 * @param {Function} setDashboardsValues - updates the given key value pairs in values for dashboard id in the store
 * @param {Object} widgets - the dashboard widgets
 */
export const setWidgetsGlobalDate = ({
    dateRangeType,
    globalDateCustomFrom,
    globalDateCustomTo,
    id,
    setDashboardsValues,
    widgets,
}) => {
    // https://stackoverflow.com/a/40470792/6201291
    // https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Object/assign#warning_for_deep_clone
    const newWidgets = JSON.parse(JSON.stringify(widgets))
    Object.values(newWidgets).forEach(widget => {
        if (widget.data_sources) {
            widget.data_sources = (
                widget.data_sources.map((dataSource) => {
                    // custom map visualizations have a data_source but do not have a safed_search
                    if (!dataSource.safed_search) {
                        return dataSource
                    }

                    dataSource.additional_filters = {}

                    // DateRangeType enum
                    if (dateRangeType) {
                        const dateRangeField = DjangIO.app.search.types.QuorumDataTypeOrdering.by_data_type_date_range[
                            dataSource.safed_search.data_type
                        ]

                        if (dateRangeField) {
                            dataSource.additional_filters = {
                                [dateRangeField]: dateRangeType,
                            }
                        }
                    // Custom Range
                    } else if (
                        !dateRangeType &&
                        (
                            globalDateCustomFrom ||
                            globalDateCustomTo
                        )
                    ) {
                        const customDateField = DjangIO.app.search.types.QuorumDataTypeOrdering.by_data_type_default_date[
                            dataSource.safed_search.data_type
                        ]

                        if (customDateField) {
                            dataSource.additional_filters = {
                                ...(
                                    globalDateCustomFrom &&
                                    {
                                        // https://stackoverflow.com/a/49909251/6201291
                                        [`${customDateField}__gt`]: globalDateCustomFrom.clone().startOf("day").format(),
                                    }
                                ),
                                ...(
                                    globalDateCustomTo &&
                                    {
                                        // https://stackoverflow.com/a/49909251/6201291
                                        [`${customDateField}__lt`]: globalDateCustomTo.clone().endOf("day").format(),
                                    }
                                ),
                            }
                        }
                    // Clear
                    } else if (
                        !dateRangeType &&
                        !globalDateCustomFrom &&
                        !globalDateCustomTo
                    ) {
                        dataSource.additional_filters = null
                    }

                    return dataSource
                })
            )
        }
    })

    setDashboardsValues({
        id,
        values: {
            isLoading: true,
            widgets: newWidgets,
        }
    })

    return newWidgets
}

export const globalDateButtonOnClick = async ({
    dateRangeType,
    globalDateCustomFrom,
    globalDateCustomTo,

    createOrUpdateDashboard,
    destroyDashboardStore,
    deletedWidgets,
    id,
    name,
    sendAction,
    setDashboardsValues,
    widgets,
}) => {
    const newWidgets = setWidgetsGlobalDate({
        dateRangeType,
        globalDateCustomFrom,
        globalDateCustomTo,

        id,
        setDashboardsValues,
        widgets,
    })

    // save global date props.widgets data_sources additional_filters changes to the database
    await saveDashboard({
        createOrUpdateDashboard,
        deletedWidgets,
        id,
        name,
        sendAction,
        setDashboardsValues,
        widgets: newWidgets,
    })

    // refresh Dashboard with new data
    destroyDashboardStore({
        id,
    })
}
